import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { apiKeysAPI } from "../../services/api";
import { Key, Trash2, Copy, Check } from "lucide-react";

const ApiKeysSettings = () => {
  const { currentWorkspace } = useAuth();
  const clientId = currentWorkspace?.id;

  const [keys, setKeys] = useState([]);
  const [meta, setMeta] = useState({ count: 0, max: 10 });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyExpiry, setNewKeyExpiry] = useState("");
  const [createdToken, setCreatedToken] = useState(null);
  const [copied, setCopied] = useState(false);

  const loadKeys = useCallback(async () => {
    if (!clientId) return;
    try {
      const res = await apiKeysAPI.list(clientId);
      setKeys(res.data || []);
      setMeta(res.meta || { count: 0, max: 10 });
    } catch (e) {
      console.error("Failed to load API keys:", e);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newKeyName.trim()) return;
    setCreating(true);
    try {
      const res = await apiKeysAPI.create({
        client_id: clientId,
        name: newKeyName.trim(),
        expires_at: newKeyExpiry || null,
      });
      setCreatedToken(res.data.token);
      setNewKeyName("");
      setNewKeyExpiry("");
      setShowCreateForm(false);
      loadKeys();
    } catch (e) {
      alert(e.response?.data?.error || "Failed to create API key");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (key) => {
    if (!window.confirm(`Delete API key "${key.name}"? This cannot be undone.`)) return;
    try {
      await apiKeysAPI.delete(key.id);
      loadKeys();
    } catch (e) {
      alert("Failed to delete API key");
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(createdToken);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!clientId) {
    return <div className="p-4 text-sm text-muted-foreground">No workspace selected.</div>;
  }

  if (loading) {
    return <div className="p-4 text-sm text-muted-foreground">Loading API keys...</div>;
  }

  return (
    <div className="bg-card rounded-lg border border-border">
      {/* Created token modal */}
      {createdToken && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg p-4 max-w-lg w-full mx-4 shadow-xl">
            <h3 className="text-sm font-medium text-foreground mb-2">API Key Created</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Copy this token now. It won't be shown again.
            </p>
            <div className="bg-muted/50 border border-border rounded-md p-3 flex items-center gap-2">
              <code className="text-sm text-foreground break-all flex-1 font-mono">
                {createdToken}
              </code>
              <button
                onClick={handleCopy}
                className="shrink-0 p-2 hover:bg-gray-200 rounded-md transition-colors"
              >
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4 text-muted-foreground" />}
              </button>
            </div>
            <button
              onClick={() => setCreatedToken(null)}
              className="mt-4 w-full py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
            >
              Done
            </button>
          </div>
        </div>
      )}

      <div className="p-4">
        <div className="space-y-8">
          {/* Create API Key section */}
          <div className="grid grid-cols-3 gap-8">
            <div className="col-span-1">
              <h3 className="text-sm font-medium text-foreground">Create API key</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                API keys allow programmatic access to this workspace. Each key has admin-level permissions.
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                {meta.count} of {meta.max} keys used
              </p>
              <a
                href="https://docs.rubrichq.io/"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-sm text-primary hover:underline"
              >
                View API documentation →
              </a>
            </div>
            <div className="col-span-2">
              {showCreateForm ? (
                <form onSubmit={handleCreate} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Name</label>
                    <input
                      type="text"
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                      placeholder="e.g. CI/CD Pipeline, Zapier Integration"
                      className="block w-full rounded-md border-input shadow-sm focus:border-black focus:ring-black text-sm"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Expiry <span className="text-muted-foreground">(optional, defaults to 1 year)</span>
                    </label>
                    <input
                      type="date"
                      value={newKeyExpiry}
                      onChange={(e) => setNewKeyExpiry(e.target.value)}
                      min={new Date().toISOString().split("T")[0]}
                      className="block w-full rounded-md border-input shadow-sm focus:border-black focus:ring-black text-sm"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={creating || !newKeyName.trim()}
                      className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black disabled:opacity-50"
                    >
                      {creating ? "Creating..." : "Create API Key"}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowCreateForm(false); setNewKeyName(""); setNewKeyExpiry(""); }}
                      className="inline-flex justify-center py-2 px-4 border border-input shadow-sm text-sm font-medium rounded-md text-foreground bg-card hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <button
                  onClick={() => setShowCreateForm(true)}
                  disabled={meta.count >= meta.max}
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black disabled:opacity-50"
                >
                  Create API Key
                </button>
              )}
            </div>
          </div>

          {/* Divider */}
          <hr className="border-border" />

          {/* API Keys list section */}
          <div className="grid grid-cols-3 gap-8">
            <div className="col-span-1">
              <h3 className="text-sm font-medium text-foreground">Active keys</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Manage your existing API keys. Deleting a key immediately revokes access.
              </p>
            </div>
            <div className="col-span-2">
              {keys.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Key className="h-6 w-6 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No API keys yet</p>
                </div>
              ) : (
                <div className="border border-border rounded-md overflow-hidden divide-y divide-gray-100">
                  {keys.map((key) => (
                    <div key={key.id} className="flex items-center justify-between px-4 py-3 hover:bg-muted/50">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <Key className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="text-sm font-medium text-foreground">{key.name}</span>
                          {key.expired && (
                            <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">Expired</span>
                          )}
                        </div>
                        <div className="mt-0.5 text-sm text-muted-foreground flex gap-3">
                          <span>Created {new Date(key.created_at).toLocaleDateString()} by {key.created_by?.name}</span>
                          {key.last_used_at && <span>Last used {new Date(key.last_used_at).toLocaleDateString()}</span>}
                          {key.expires_at && <span>Expires {new Date(key.expires_at).toLocaleDateString()}</span>}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDelete(key)}
                        className="p-1.5 text-muted-foreground hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiKeysSettings;
